/**
 * DeleteManagedSqlDbModal Component
 *
 * Confirmation modal for deleting a managed SQL database.
 * Requires two-factor authentication verification before deletion proceeds.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteManagedSqlDb } from '../hooks/useDeleteManagedSqlDb';
import type { ManagedSqlDbSanitized } from '../../../types/managed-sql-db.types';
import type { TwoFactorRequiredResponse } from '../../../types/auth.types';
import clsx from 'clsx';

interface DeleteManagedSqlDbModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  managedSqlDb: ManagedSqlDbSanitized;
}

/**
 * Format remaining seconds as "Xm Xs"
 */
function formatTime(seconds: number): string {
  if (seconds <= 0) return 'Expired';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * DeleteManagedSqlDbModal component
 */
export const DeleteManagedSqlDbModal: React.FC<DeleteManagedSqlDbModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  managedSqlDb,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  // 2FA state
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorRequiredResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);

  const { mutate: deleteManagedSqlDb, isPending } = useDeleteManagedSqlDb(tenantId, {
    onSuccess: () => {
      handleReset();
      onClose();
    },
    onError: (error) => {
      if (twoFactorState) {
        setLocalError(error);
      } else {
        setErrorMessage(error);
      }
    },
    onTwoFactorRequired: (response) => {
      setTwoFactorState(response);
      setRemainingTime(response.expiresIn);
      setErrorMessage('');
    },
  });

  // Reset all state
  const handleReset = useCallback(() => {
    setErrorMessage('');
    setTwoFactorState(null);
    setVerificationCode('');
    setLocalError(null);
    setRemainingTime(0);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      handleReset();
    }
  }, [isOpen, handleReset]);

  // Countdown timer for 2FA
  useEffect(() => {
    if (!twoFactorState || remainingTime <= 0) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [twoFactorState, remainingTime]);

  // Step 1: Initiate deletion (triggers 2FA)
  const handleDelete = () => {
    setErrorMessage('');
    deleteManagedSqlDb({ managedSqlDbId: managedSqlDb.managedSqlDbId });
  };

  // Step 2: Submit 2FA verification code
  const handleVerify = useCallback(() => {
    if (!verificationCode || verificationCode.length !== 6) {
      setLocalError('Please enter a 6-digit verification code');
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      setLocalError('Verification code must be 6 digits');
      return;
    }

    if (remainingTime <= 0) {
      setLocalError('Verification code has expired');
      return;
    }

    setLocalError(null);
    deleteManagedSqlDb({
      managedSqlDbId: managedSqlDb.managedSqlDbId,
      verificationCodeId: twoFactorState!.codeId,
      verificationCode,
    });
  }, [verificationCode, remainingTime, twoFactorState, managedSqlDb.managedSqlDbId, deleteManagedSqlDb]);

  const handleCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setVerificationCode(value);
      setLocalError(null);
    }
  }, []);

  const handleClose = () => {
    if (!isPending) {
      handleReset();
      onClose();
    }
  };

  const hasGrants =
    (managedSqlDb.applicationGrants?.length || 0) > 0 ||
    (managedSqlDb.eltJobGrants?.length || 0) > 0;

  const isExpired = twoFactorState ? remainingTime <= 0 : false;
  const displayError = localError || (twoFactorState ? errorMessage : null);

  // 2FA Verification Phase
  if (twoFactorState) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="md">
        <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }}>
          <Modal.Header>Two-Factor Authentication Required</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  To delete <span className="font-semibold">{managedSqlDb.managedSqlDbName}</span>, please
                  enter the verification code sent to <strong>{twoFactorState.destination}</strong>.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="delete-db-verification-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Verification Code
                </label>
                <input
                  id="delete-db-verification-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={verificationCode}
                  onChange={handleCodeChange}
                  disabled={isPending || isExpired}
                  placeholder="000000"
                  autoComplete="off"
                  autoFocus
                  className={clsx(
                    'block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest rounded-lg border shadow-sm',
                    'focus:outline-none focus:ring-2',
                    isExpired || displayError
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
                    'dark:bg-gray-700 dark:border-gray-600 dark:text-white',
                    (isPending || isExpired) && 'opacity-50 cursor-not-allowed'
                  )}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Enter the 6-digit code
                </p>
              </div>

              {/* Timer and attempts info */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Expires in:</span>
                  <span
                    className={clsx(
                      'font-medium',
                      isExpired
                        ? 'text-red-600 dark:text-red-400'
                        : remainingTime <= 30
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {formatTime(remainingTime)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Attempts:</span>
                  <span
                    className={clsx(
                      'font-medium',
                      twoFactorState.attemptsRemaining <= 2
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {twoFactorState.attemptsRemaining} left
                  </span>
                </div>
              </div>

              {/* Error message */}
              {displayError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{displayError}</p>
                </div>
              )}

              {/* Expired warning */}
              {isExpired && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    This verification code has expired. Please close this dialog and try again.
                  </p>
                </div>
              )}

              {/* Low attempts warning */}
              {twoFactorState.attemptsRemaining <= 2 && !isExpired && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Warning: Only {twoFactorState.attemptsRemaining}{' '}
                    {twoFactorState.attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining before
                    this code is locked.
                  </p>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="danger"
                loading={isPending}
                disabled={isExpired || verificationCode.length !== 6}
              >
                Verify & Delete
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }

  // Confirmation Phase (initial)
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Delete Managed SQL Database</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Warning Icon */}
          <div className="flex items-center justify-center">
            <div className="rounded-full bg-red-100 p-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
          </div>

          {/* Warning Message */}
          <div className="text-center">
            <p className="text-sm text-gray-900">
              Are you sure you want to delete the database{' '}
              <span className="font-semibold">{managedSqlDb.managedSqlDbName}</span>?
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Code: <code className="bg-gray-100 px-1 rounded">{managedSqlDb.managedSqlDbCode}</code>
            </p>
          </div>

          {/* Active Grants Warning */}
          {hasGrants && (
            <Alert variant="warning">
              This database has active grants. All grants must be revoked before the database can
              be deleted.
            </Alert>
          )}

          {/* Danger Warning */}
          {!hasGrants && (
            <Alert variant="error">
              <strong>This action cannot be undone.</strong> The PostgreSQL database and all its
              data will be permanently deleted. A verification code will be sent to your email.
            </Alert>
          )}

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={isPending}
              disabled={hasGrants}
            >
              Delete Database
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
